import { FILTER_TYPES } from '@mm/state/model/AudiencesModel';
import classNames from 'classnames';
import React, { useCallback, useState } from 'react';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import CheckBox from '../../../../../../common/components/Checkbox';
import {
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '../../../../../../common/modals/modal-helpers';
import {
  AddFilterType,
  LocalSystemFilterItem,
  UpdateFilterType,
} from '../../../sharedFilterTypes';
import './platformsFilterModal.pcss';

type Props = {
  toggle: () => void;
  onFinish: AddFilterType;
  onUpdateFilter: UpdateFilterType;
  selectedFilterId?: number | null;
  nonModalUse?: boolean;
  selectedFilter?: LocalSystemFilterItem;
  className?: string;
};

export const platforms: {
  name: 'facebook' | 'sms' | 'web_chat' | 'instagram' | 'email';
  text: string;
}[] = [
  {
    name: 'facebook',
    text: 'Facebook Messenger',
  },
  {
    name: 'sms',
    text: 'SMS',
  },
  {
    name: 'web_chat',
    text: 'WebChat',
  },
  {
    name: 'instagram',
    text: 'Instagram',
  },
  {
    name: 'email',
    text: 'Email',
  },
];

export const PlatformsFilterModal = ({
  toggle,
  onFinish,
  onUpdateFilter,
  selectedFilterId,
  nonModalUse,
  selectedFilter,
  className,
}: Props) => {
  const [isSaving, setIsSaving] = useState(false);
  const [selectedPlatforms, setSelectedPlatforms] = useState<
    ('facebook' | 'sms' | 'web_chat' | 'instagram' | 'email')[]
  >(
    (selectedFilter?.value as (
      | 'facebook'
      | 'sms'
      | 'web_chat'
      | 'instagram'
      | 'email'
    )[]) || ['facebook'],
  );

  const header = <>Choose Platform</>;

  const togglePlatform = useCallback(
    (platform: 'facebook' | 'sms' | 'web_chat' | 'instagram' | 'email') => {
      const isPlatfromAdded = selectedPlatforms.includes(platform);
      isPlatfromAdded
        ? setSelectedPlatforms(selectedPlatforms.filter(p => p !== platform))
        : setSelectedPlatforms([...selectedPlatforms, platform]);
    },
    [selectedPlatforms],
  );

  const body = (
    <>
      {platforms.map(platform => (
        <div
          className={classNames(
            className,
            'platforms-filter-modal__check-area',
          )}
        >
          <CheckBox
            isChecked={selectedPlatforms.some(p => p === platform.name)}
            onClick={() => togglePlatform(platform.name)}
          />

          <span className="platforms-filter-modal__check-area-text">
            {platform.text}
          </span>
        </div>
      ))}
    </>
  );

  const onSave = useCallback(() => {
    setIsSaving(true);
    onFinish({
      value: selectedPlatforms,
      filterType: FILTER_TYPES.platforms,
    });
    toggle();
  }, [selectedPlatforms]);

  const onUpdate = useCallback(() => {
    setIsSaving(true);
    if (selectedFilterId) {
      onUpdateFilter(selectedFilterId, {
        value: selectedPlatforms,
        filterType: FILTER_TYPES.platforms,
      });
    }
    toggle();
  }, [selectedPlatforms]);

  const footer = (
    <div className="platforms-filter-modal__buttons">
      <PrimaryButton color={ButtonColor.secondaryBlue} onClick={toggle}>
        Cancel
      </PrimaryButton>
      {!selectedFilterId && (
        <PrimaryButton disabled={isSaving} onClick={onSave}>
          Save
        </PrimaryButton>
      )}
      {selectedFilterId && (
        <PrimaryButton disabled={isSaving} onClick={onUpdate}>
          Update
        </PrimaryButton>
      )}
    </div>
  );

  let content = (
    <>
      <ModalHeader className="platforms-filter-modal__header">
        {header}
      </ModalHeader>
      <ModalBody className="platforms-filter-modal__body">{body}</ModalBody>
      <ModalFooter className="platforms-filter-modal__footer">
        {footer}
      </ModalFooter>
    </>
  );
  if (nonModalUse) {
    content = (
      <>
        {body}
        {footer}
      </>
    );
  }

  return (
    <div className={classNames(className, 'platforms-filter-modal')}>
      {content}
    </div>
  );
};
